import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading';
import { AddProjectModal, type CreatedProject } from './AddProjectModal';

export interface ProjectAccessOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface ProjectAccessFieldProps {
  options: ProjectAccessOption[];
  value: string[];
  onValueChange: (ids: string[]) => void;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onProjectCreated?: (project: CreatedProject) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

/**
 * Project Access assignment control used by the Create/Edit User form and the
 * Manage Project Access dialog. Assigned projects are shown as compact cards
 * (with an optional sublabel and a Remove action) so the admin can see exactly
 * what is assigned without scanning a large list. Unassigned projects are
 * offered through a searchable checklist, and an "+ Add Project" action opens
 * a quick-create modal for the existing project master.
 */
export function ProjectAccessField({
  options,
  value,
  onValueChange,
  isLoading = false,
  isError = false,
  onRetry,
  onProjectCreated,
  searchPlaceholder = 'Search project...',
  emptyMessage = 'No active projects available.',
}: ProjectAccessFieldProps) {
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const selectedCount = value.length;
  const allChecked = selectedCount > 0 && selectedCount === options.length;
  const indeterminate = selectedCount > 0 && selectedCount < options.length;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const toggle = (optValue: string) => {
    onValueChange(value.includes(optValue) ? value.filter((v) => v !== optValue) : [...value, optValue]);
  };

  const toggleAll = () => {
    onValueChange(allChecked ? [] : options.map((o) => o.value));
  };

  const normalized = query.trim().toLowerCase();
  const assigned = useMemo(() => options.filter((o) => value.includes(o.value)), [options, value]);
  const available = useMemo(() => {
    const unassigned = options.filter((o) => !value.includes(o.value));
    return normalized ? unassigned.filter((o) => o.label.toLowerCase().includes(normalized)) : unassigned;
  }, [options, value, normalized]);

  const assignedLabel = selectedCount === 0 ? 'No projects assigned yet.' : selectedCount === 1 ? '1 project assigned' : `${selectedCount} projects assigned`;

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label="Search projects"
            className="pl-9"
          />
        </div>
        <Button type="button" variant="outline" className="sm:w-auto" onClick={() => setAddOpen(true)} data-testid="add-project-button">
          + Add Project
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted">
          <LoadingSpinner size={16} />
          Loading active projects...
        </div>
      ) : isError ? (
        <div className="space-y-2 text-sm">
          <p className="text-error">Unable to load active projects.</p>
          {onRetry && (
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* ── Assigned Projects ─────────────────────────────────────────── */}
          <div>
            <p className="mb-2 text-sm font-medium text-primary">{assignedLabel}</p>
            {assigned.length === 0 ? (
              <p className="rounded-lg border border-dashed border-default px-3 py-2 text-sm text-muted">
                Select projects below or add a new one to grant access.
              </p>
            ) : (
              <ul className="space-y-2">
                {assigned.map((opt) => (
                  <li
                    key={opt.value}
                    className="flex items-center gap-3 rounded-lg border border-default bg-surface-secondary px-3 py-2"
                  >
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggle(opt.value)}
                        aria-label={opt.label}
                        className="h-4 w-4 shrink-0 accent-blue-600"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-primary">{opt.label}</span>
                        {opt.sublabel && <span className="block truncate text-xs text-muted">{opt.sublabel}</span>}
                      </span>
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${opt.label}`}
                      onClick={() => toggle(opt.value)}
                      className="shrink-0 text-muted hover:text-destructive"
                    >
                      <X size={14} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Available projects ────────────────────────────────────────── */}
          {options.length === 0 ? (
            <p className="text-sm text-muted">{emptyMessage}</p>
          ) : (
            <div className="rounded-lg border border-default">
              <label className="flex cursor-pointer items-center gap-2.5 border-b border-default px-3 py-2.5 hover:bg-hover">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleAll}
                  className="h-4 w-4 accent-blue-600"
                />
                <span className="text-sm font-medium text-primary">Select All</span>
              </label>
              {available.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted">{normalized ? 'No matching projects.' : 'All projects are assigned.'}</p>
              ) : (
                <ul className="max-h-52 overflow-y-auto">
                  {available.map((opt) => (
                    <li key={opt.value}>
                      <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 hover:bg-hover">
                        <input
                          type="checkbox"
                          checked={false}
                          onChange={() => toggle(opt.value)}
                          aria-label={opt.label}
                          className="h-4 w-4 accent-blue-600"
                        />
                        <span className="truncate text-sm text-primary">{opt.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      <AddProjectModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(project) => {
          if (project.id) onValueChange(value.includes(project.id) ? value : [...value, project.id]);
          onProjectCreated?.(project);
        }}
      />
    </div>
  );
}